/**
 * Centralized API Client
 *
 * This module provides a configured axios instance and typed helper functions
 * for all API interactions. Uses environment variables for configuration.
 *
 * @example
 * import { api, apiHelpers } from '@/lib/api';
 *
 * // Direct axios usage
 * const response = await api.get('/custom-endpoint');
 *
 * // Typed helper functions
 * const { data } = await apiHelpers.getProjects({ page: 1, limit: 10 });
 */

import axios, { type AxiosResponse } from 'axios';
import { toast } from 'sonner';
import { useAuthStore, getAuthToken } from '@/stores/auth';
import type {
  ApiResponse,
  Project,
  User,
  UserRulesNoticeContent,
  UserRulesNoticeLanguage,
  UserRulesNoticeStatus,
  Plugin,
  ProjectPlugin,
  NewsletterSubscriber,
  NewsletterStats,
  ContactSubmission,
  ContactStats,
  ContactStatus,
  ConsentSettings,
  ConsentRecord,
  ConsentAnalytics,
  PaginationParams,
  PaginationMeta,
  CreateProjectData,
  UpdateProjectData,
  ConnectProjectData,
  CreateUserData,
  UpdateUserData,
  UserDeletionImpact,
  AssignProjectData,
  InstallPluginData,
  GoogleTagConnectStatus,
  GoogleTagConnection,
  UpdatePluginConfigData,
  NewsletterQueryParams,
  ContactQueryParams,
  UpdateContactData,
  BulkContactActionData,
  ConsentQueryParams,
  UpdateConsentSettingsData,
  BlogPost,
  BlogCategory,
  BlogSuggestionsResponse,
  BlogSuggestion,
  ManualBlogTopicInput,
  IndustryProfile,
  BlogTag,
  CreateBlogPostData,
  UpdateBlogPostData,
  CreateBlogCategoryData,
  CreateBlogTagData,
  PluginManifestResponse,
  HealthCheckResult,
  DnsMigration,
  TopicMapResponse,
} from '@/types';

import type { ProjectAnalyticsStatus, TokenBundle } from '@/lib/yom/types';
import type { KpiRecomputeStats, PlatformKpis } from '@/lib/platformKpis';
import {
  normalizeAnalyticsStatus,
  normalizeClaimLink,
  normalizePlatformTokens,
  normalizeProjectEmbedToken,
} from '@/lib/yom/normalize';

// Re-export DNS migration types so callers can `import { DnsMigration } from '@/lib/api'`.
export type { DnsMigration, DnsMigrationState } from '@/types';

// ============================================================================
// Configuration
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * Pre-configured axios instance with base URL and default headers
 */
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// Request Interceptor - Auth Token Injection
// ============================================================================

api.interceptors.request.use(
  (config) => {
    // Ghost-vs-normal resolution lives in `getAuthToken` (stores/auth) so this
    // interceptor and the chatbot WebSocket can never disagree about which
    // token a tab is acting as.
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================================================
// Response Interceptor - Token Refresh & Error Handling
// ============================================================================

// Track if we're currently refreshing to prevent concurrent refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip auth handling for login/refresh-style endpoints to prevent infinite
    // loops. /auth/cli/* is not one of those: the calls this SPA makes there
    // (reading and approving a device request, listing/revoking tokens) need
    // the signed-in session, so an expired access token must refresh and retry.
    // Without this the /device approval page showed "Code not found / Invalid
    // or expired token". (The public device/token endpoints are only ever
    // called by external tools, never by this app.)
    const url = originalRequest?.url ?? '';
    const isAuthEndpoint = url.includes('/auth/') && !url.includes('/auth/cli/');
    if (isAuthEndpoint) {
      // If refresh endpoint failed with 401, redirect to login
      if (originalRequest?.url?.includes('/auth/refresh') && error.response?.status === 401) {
        useAuthStore.getState().clearAuth();
        toast.error('Session expired. Please log in again.');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Handle 401 with automatic token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Ghost sessions: no refresh, just expire
      if (sessionStorage.getItem('ghost_token')) {
        sessionStorage.removeItem('ghost_token');
        sessionStorage.removeItem('ghost_user');
        sessionStorage.removeItem('ghost_impersonated_by');
        sessionStorage.removeItem('auth-storage');
        useAuthStore.getState().clearAuth();
        toast.error('Ghost session expired. Close this tab to return to your admin session.');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Check if we have a refresh token - if not, just reject
      const { refreshToken } = useAuthStore.getState();
      if (!refreshToken) {
        useAuthStore.getState().clearAuth();
        toast.error('Please log in to continue.');
        // Don't redirect - let the component/ProtectedRoute handle it
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue requests while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await useAuthStore.getState().refreshTokens();
        const { accessToken } = useAuthStore.getState();
        processQueue(null, accessToken);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Refresh failed - DON'T redirect immediately
        // Let the component handle the error and show a message
        // The user may have unsaved work they want to copy first
        useAuthStore.getState().clearAuth();
        toast.error('Session expired. Please log in again.');
        // Don't redirect here - let the ProtectedRoute handle it after user sees the error
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// Type Helpers
// ============================================================================

type PaginatedResponse<T> = ApiResponse<T[]> & { meta?: PaginationMeta };

/**
 * One pricing tier from GET /plugins/projects/:projectId/:pluginId/plans.
 *
 * `priceMajor` is in MAJOR currency units (1 credit = 1 major unit) — the
 * deliberate exception to the minor-units-everywhere money contract. The field
 * name matches the backend catalog (`PluginTier.priceMajor`) so a wire/consumer
 * mismatch is a compile error, not a runtime `undefined.toLocaleString()` crash.
 */
export interface PluginPlanTier {
  tier: string;
  displayName: string;
  priceMajor: number;
  currency: string;
  interval: 'month' | 'year';
  features: string[];
  recommended?: boolean;
}

export interface PluginPlansResponse {
  pluginId: string;
  currency: string;
  hasPaidTiers: boolean;
  /** The "why subscription" recurring-value story, shown above the tier grid. */
  recurringValue?: { headline: string; points: string[] } | null;
  tiers: PluginPlanTier[];
}

// ============================================================================
// API Helper Functions - Typed
// ============================================================================

export const apiHelpers = {
  // -------------------------------------------------------------------------
  // Projects
  // -------------------------------------------------------------------------

  /** Get paginated list of projects */
  getProjects: (params?: PaginationParams): Promise<AxiosResponse<PaginatedResponse<Project>>> =>
    api.get('/projects', { params }),

  /** Get single project by ID */
  getProject: (id: string): Promise<AxiosResponse<ApiResponse<Project>>> =>
    api.get(`/projects/${id}`),

  /** SUPER_ADMIN: full billing summary for a team (subscription + gateway subs + invoices + credits) */
  getTeamBillingSummary: (teamId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(`/admin/billing/teams/${teamId}/summary`),

  /** Create a new project */
  createProject: (data: CreateProjectData): Promise<AxiosResponse<ApiResponse<Project>>> =>
    api.post('/projects', data),

  /** Connect to existing project via API key */
  connectProject: (data: ConnectProjectData): Promise<AxiosResponse<ApiResponse<Project>>> =>
    api.post('/projects/connect', data),

  /** Update project details */
  updateProject: (
    id: string,
    data: UpdateProjectData
  ): Promise<AxiosResponse<ApiResponse<Project>>> => api.patch(`/projects/${id}`, data),

  /** Delete a project */
  deleteProject: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.delete(`/projects/${id}`),

  /** Regenerate project API key */
  regenerateApiKey: (id: string): Promise<AxiosResponse<ApiResponse<{ apiKey: string }>>> =>
    api.post(`/projects/${id}/regenerate-key`),

  /** SUPER_ADMIN: suspend a project — site + public API + crons go dark, data kept */
  suspendProject: (id: string, reason?: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/projects/${id}/suspend`, { reason: reason || undefined }),

  /** SUPER_ADMIN: lift a suspension — restores the pre-suspension status */
  unsuspendProject: (id: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/projects/${id}/unsuspend`),

  /** SUPER_ADMIN: archive a project — tears down live infra (hostnames, Worker,
   *  repo), keeps all data. Site serves 410. Reversible via unarchive. */
  archiveProject: (id: string, reason?: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/projects/${id}/archive`, { reason: reason || undefined }),

  /** SUPER_ADMIN: unarchive — re-provisions hostnames + repo and triggers a
   *  rebuild; the site returns once the build completes (minutes). */
  unarchiveProject: (id: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/projects/${id}/unarchive`),

  /**
   * Get metered usage counts for a project.
   * Pass `resource` for history + current count for that specific resource.
   */
  getProjectUsage: (
    id: string,
    params?: { period?: string; resource?: string; months?: number }
  ): Promise<
    AxiosResponse<
      ApiResponse<{
        projectId: string;
        period: string;
        breakdown: Array<{ resource: string; category: string | null; total: number }>;
        totals: Record<string, number>;
        // Only present when `resource` param supplied:
        resource?: string;
        currentCount?: number;
        history?: Array<{ period: string; total: number }>;
      }>
    >
  > => api.get(`/projects/${id}/usage`, { params }),

  /** Get global stats across all accessible projects */
  getGlobalStats: (): Promise<
    AxiosResponse<ApiResponse<{ newsletterSubscribers: number; contactSubmissions: number }>>
  > => api.get('/projects/stats/global'),

  // -------------------------------------------------------------------------
  // Quarterly rules notice (IT Rules 3(1)(c)); status rides the user object
  // -------------------------------------------------------------------------

  getUserRulesNotice: (): Promise<AxiosResponse<ApiResponse<UserRulesNoticeContent>>> =>
    api.get('/user-rules-notice'),

  acknowledgeUserRulesNotice: (data: {
    version: string;
    language: UserRulesNoticeLanguage;
  }): Promise<AxiosResponse<ApiResponse<UserRulesNoticeStatus & { recorded: boolean }>>> =>
    api.post('/user-rules-notice/acknowledge', data),

  // -------------------------------------------------------------------------
  // Users
  // -------------------------------------------------------------------------

  /** Get paginated list of users */
  getUsers: (
    params?: PaginationParams & { projectId?: string }
  ): Promise<AxiosResponse<PaginatedResponse<User>>> => api.get('/users', { params }),

  /** Get single user by ID */
  getUser: (id: string): Promise<AxiosResponse<ApiResponse<User>>> => api.get(`/users/${id}`),

  /** Create a new user */
  createUser: (data: CreateUserData): Promise<AxiosResponse<ApiResponse<User>>> =>
    api.post('/users', data),

  /** Update user details */
  updateUser: (id: string, data: UpdateUserData): Promise<AxiosResponse<ApiResponse<User>>> =>
    api.patch(`/users/${id}`, data),

  /**
   * What would deleting this user destroy? Call BEFORE the confirm dialog:
   * `sharedTeams` are teams other people actively work in, and each one needs
   * an ownership transfer before the account can go.
   */
  getUserDeletionImpact: (id: string): Promise<AxiosResponse<ApiResponse<UserDeletionImpact>>> =>
    api.get(`/users/${id}/deletion-impact`),

  /**
   * Delete a user and everything they solely own.
   *
   * `transferOwnership` maps teamId → the user who should take it over; it is
   * REQUIRED for every shared team, otherwise the API refuses with 409
   * OWNERSHIP_TRANSFER_REQUIRED rather than deleting colleagues' projects.
   */
  deleteUser: (
    id: string,
    transferOwnership?: Record<string, string>
  ): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.delete(`/users/${id}`, {
      data: transferOwnership ? { transferOwnership } : undefined,
    }),

  /** Assign user to a project */
  assignUserToProject: (
    userId: string,
    data: AssignProjectData
  ): Promise<AxiosResponse<ApiResponse<User>>> => api.post(`/users/${userId}/assign-project`, data),

  /** Remove user from a project */
  removeUserFromProject: (
    userId: string,
    projectId: string
  ): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.delete(`/users/${userId}/projects/${projectId}`),

  /** Impersonate / ghost-login as a user (SUPER_ADMIN only) */
  impersonateUser: (
    userId: string
  ): Promise<
    AxiosResponse<ApiResponse<{ ghostToken: string; user: User; impersonatedBy: string }>>
  > => api.post(`/users/${userId}/impersonate`),

  // -------------------------------------------------------------------------
  // Plugins
  // -------------------------------------------------------------------------

  /** Unified monthly statement: base plan + plugins + add-ons in one view */
  getBillingStatement: (): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get('/billing/statement'),

  /** Gateway-first checkout for any product (returns a gateway authorization URL) */
  checkoutProduct: (
    productType: 'platform' | 'plugin' | 'addon',
    productId: string,
    tier: string,
    /** Required for plugin/addon products — the project the tier is bought
     *  for; the payment webhook activates the entitlement on it. */
    projectId?: string
  ): Promise<
    AxiosResponse<ApiResponse<{ checkoutUrl?: string; gateway: string; status: string }>>
  > =>
    api.post('/billing/checkout', {
      productType,
      productId,
      tier,
      projectId,
      successUrl: `${window.location.origin}${window.location.pathname}?checkout=success`,
      cancelUrl: `${window.location.origin}${window.location.pathname}?checkout=cancelled`,
    }),

  /** Get all available plugins */
  getAvailablePlugins: (): Promise<AxiosResponse<ApiResponse<Plugin[]>>> =>
    api.get('/plugins/available'),

  /** Get plugins installed for a project */
  getProjectPlugins: (projectId: string): Promise<AxiosResponse<ApiResponse<ProjectPlugin[]>>> =>
    api.get(`/plugins/projects/${projectId}`),

  /** Install a plugin for a project */
  installPlugin: (
    projectId: string,
    data: InstallPluginData
  ): Promise<AxiosResponse<ApiResponse<ProjectPlugin>>> =>
    api.post(`/plugins/projects/${projectId}`, data),

  /** Update plugin configuration */
  updatePluginConfig: (
    projectId: string,
    pluginId: string,
    data: UpdatePluginConfigData
  ): Promise<AxiosResponse<ApiResponse<ProjectPlugin>>> =>
    api.patch(`/plugins/projects/${projectId}/${pluginId}`, data),

  /** Uninstall a plugin from a project */
  uninstallPlugin: (
    projectId: string,
    pluginId: string
  ): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.delete(`/plugins/projects/${projectId}/${pluginId}`),

  /** Toggle plugin enabled/disabled status */
  togglePlugin: (
    projectId: string,
    pluginId: string
  ): Promise<AxiosResponse<ApiResponse<ProjectPlugin>>> =>
    api.post(`/plugins/projects/${projectId}/${pluginId}/toggle`),

  /** Sync plugin theme/config from project brand kit */
  syncPluginBrandKit: (
    projectId: string,
    pluginId: string
  ): Promise<AxiosResponse<ApiResponse<{ applied: Record<string, any>; message: string }>>> =>
    api.post(`/plugins/projects/${projectId}/${pluginId}/sync-brand-kit`),

  /** Get full manifest for an installed plugin */
  getPluginManifest: (
    projectId: string,
    pluginId: string
  ): Promise<AxiosResponse<ApiResponse<PluginManifestResponse>>> =>
    api.get(`/plugins/projects/${projectId}/manifest/${pluginId}`),

  /** Check plugin health */
  getPluginHealth: (
    projectId: string,
    pluginId: string
  ): Promise<AxiosResponse<ApiResponse<HealthCheckResult>>> =>
    api.get(`/plugins/projects/${projectId}/${pluginId}/health`),

  // Plugin usage is fetched from each plugin's own usageSummary endpoint
  // (see docs/PLUGIN-CONTRACT.md §6). No Tyashin-side usage helper here.

  // ── Reseller billing (Phase 3 — docs/PLUGIN-CONTRACT.md §9) ────

  /** List available pricing tiers for a plugin (in team's currency) */
  getPluginPlans: (
    projectId: string,
    pluginId: string,
    currency?: string
  ): Promise<AxiosResponse<ApiResponse<PluginPlansResponse>>> =>
    api.get(`/plugins/projects/${projectId}/${pluginId}/plans`, {
      params: currency ? { currency } : undefined,
    }),

  /** Subscribe to a plugin tier */
  subscribeToPluginTier: (
    projectId: string,
    pluginId: string,
    tier: string,
    paymentMethod?: 'credits' | 'gateway'
  ): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/plugins/projects/${projectId}/${pluginId}/subscribe`, {
      tier,
      paymentMethod,
    }),

  /** Cancel a plugin subscription (at end of period) */
  cancelPluginSubscription: (
    projectId: string,
    pluginId: string
  ): Promise<AxiosResponse<ApiResponse<{ message: string; effectiveAt?: string }>>> =>
    api.post(`/plugins/projects/${projectId}/${pluginId}/cancel`),

  /** Get current plugin subscription state */
  getPluginSubscription: (
    projectId: string,
    pluginId: string
  ): Promise<
    AxiosResponse<
      ApiResponse<{
        pluginId: string;
        plan: string;
        price: number;
        currency: string;
        status: 'active' | 'cancelled' | 'past_due';
        subscribedAt: string;
        currentPeriodEnd: string;
        cancelAtPeriodEnd?: boolean;
        paymentMethod: 'credits' | 'gateway' | 'free';
      } | null>
    >
  > => api.get(`/plugins/projects/${projectId}/${pluginId}/subscription`),

  // ── OAuth integrations (Phase 4c) ─────────────────────────────

  /**
   * List OAuth-provider properties available for selection after install.
   * Currently only used by google-analytics — returns GA4 accounts + properties.
   */
  getOAuthProperties: (
    projectId: string,
    pluginId: string
  ): Promise<
    AxiosResponse<
      ApiResponse<{
        accounts: Array<{
          accountId: string;
          accountName: string;
          properties: Array<{
            propertyId: string;
            displayName: string;
            measurementId: string;
            websiteUrl?: string;
          }>;
        }>;
        userInfo: { email: string; name?: string; picture?: string } | null;
      }>
    >
  > => api.get(`/plugins/projects/${projectId}/${pluginId}/oauth-properties`),

  // ── Google Tag: "Connect with Google" (optional post-install) ────────────

  googleTagConnection: (
    projectId: string
  ): Promise<AxiosResponse<ApiResponse<GoogleTagConnectStatus>>> =>
    api.get(`/plugins/projects/${projectId}/google-tag/connection`),

  googleTagConnect: (
    projectId: string
  ): Promise<AxiosResponse<ApiResponse<{ redirectUrl: string }>>> =>
    api.post(`/plugins/projects/${projectId}/google-tag/connect`),

  googleTagSelectProperty: (
    projectId: string,
    propertyId: string
  ): Promise<AxiosResponse<ApiResponse<{ connection: GoogleTagConnection }>>> =>
    api.post(`/plugins/projects/${projectId}/google-tag/select-property`, { propertyId }),

  googleTagCreateProperty: (
    projectId: string,
    data: {
      displayName: string;
      websiteUrl: string;
      timeZone?: string;
      currencyCode?: string;
      accountId?: string;
    }
  ): Promise<AxiosResponse<ApiResponse<{ connection: GoogleTagConnection }>>> =>
    api.post(`/plugins/projects/${projectId}/google-tag/create-property`, data),

  googleTagDisconnect: (
    projectId: string
  ): Promise<AxiosResponse<ApiResponse<{ discarded: boolean }>>> =>
    api.delete(`/plugins/projects/${projectId}/google-tag/connection`),

  // -------------------------------------------------------------------------
  // Newsletter
  // -------------------------------------------------------------------------

  /** Get newsletter subscribers for a project */
  getSubscribers: (
    projectId: string,
    params?: NewsletterQueryParams
  ): Promise<AxiosResponse<PaginatedResponse<NewsletterSubscriber>>> =>
    api.get(`/newsletter/${projectId}`, { params }),

  /** Get newsletter statistics for a project */
  getSubscriberStats: (projectId: string): Promise<AxiosResponse<ApiResponse<NewsletterStats>>> =>
    api.get(`/newsletter/${projectId}/stats`),

  /** Export subscribers to file (CSV/JSON) */
  exportSubscribers: (
    projectId: string,
    format: 'csv' | 'json' = 'csv'
  ): Promise<AxiosResponse<Blob>> =>
    api.get(`/newsletter/${projectId}/export`, {
      params: { format },
      responseType: 'blob',
    }),

  // -------------------------------------------------------------------------
  // Contact
  // -------------------------------------------------------------------------

  /** Get contact submissions for a project */
  getContacts: (
    projectId: string,
    params?: ContactQueryParams
  ): Promise<AxiosResponse<PaginatedResponse<ContactSubmission>>> =>
    api.get(`/contact/${projectId}`, { params }),

  /** Get single contact submission */
  getContact: (
    projectId: string,
    id: string
  ): Promise<AxiosResponse<ApiResponse<ContactSubmission>>> =>
    api.get(`/contact/${projectId}/${id}`),

  /** Update contact submission status */
  updateContact: (
    projectId: string,
    id: string,
    data: UpdateContactData
  ): Promise<AxiosResponse<ApiResponse<ContactSubmission>>> =>
    api.patch(`/contact/${projectId}/${id}`, data),

  /** Delete a contact submission */
  deleteContact: (projectId: string, id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.delete(`/contact/${projectId}/${id}`),

  /** Perform bulk action on contact submissions */
  bulkContactAction: (
    projectId: string,
    data: BulkContactActionData
  ): Promise<AxiosResponse<ApiResponse<{ affected: number }>>> =>
    api.post(`/contact/${projectId}/bulk`, data),

  /** Get contact statistics for a project */
  getContactStats: (projectId: string): Promise<AxiosResponse<ApiResponse<ContactStats>>> =>
    api.get(`/contact/${projectId}/stats`),

  /** Export leads as CSV (Starter+). Returns the raw CSV blob. */
  exportContacts: (
    projectId: string,
    params?: { status?: ContactStatus }
  ): Promise<AxiosResponse<Blob>> =>
    api.get(`/contact/${projectId}/export.csv`, { params, responseType: 'blob' }),

  // -------------------------------------------------------------------------
  // Consent
  // -------------------------------------------------------------------------

  /** Get consent settings for a project */
  getConsentSettings: (projectId: string): Promise<AxiosResponse<ApiResponse<ConsentSettings>>> =>
    api.get(`/consent/settings/${projectId}`),

  /** Update consent settings */
  updateConsentSettings: (
    projectId: string,
    data: UpdateConsentSettingsData
  ): Promise<AxiosResponse<ApiResponse<ConsentSettings>>> =>
    api.patch(`/consent/settings/${projectId}`, data),

  /** Get consent records for a project */
  getConsentRecords: (
    projectId: string,
    params?: ConsentQueryParams
  ): Promise<AxiosResponse<PaginatedResponse<ConsentRecord>>> =>
    api.get(`/consent/records/${projectId}`, { params }),

  /** Get consent analytics for a project */
  getConsentAnalytics: (projectId: string): Promise<AxiosResponse<ApiResponse<ConsentAnalytics>>> =>
    api.get(`/consent/analytics/${projectId}`),

  /** Export consent records to file */
  exportConsentRecords: (
    projectId: string,
    format: 'json' | 'csv' = 'json'
  ): Promise<AxiosResponse<Blob>> =>
    api.get(`/consent/export/${projectId}`, {
      params: { format },
      responseType: 'blob',
    }),

  // -------------------------------------------------------------------------
  // Blog / CMS
  // -------------------------------------------------------------------------

  /** Get blog posts for a project */
  getBlogPosts: (
    projectId: string,
    params?: {
      page?: number;
      limit?: number;
      status?: string;
      categoryId?: string;
      search?: string;
    }
  ): Promise<AxiosResponse<PaginatedResponse<BlogPost>>> =>
    api.get(`/blog/projects/${projectId}/posts`, { params }),

  /** Get a single blog post */
  getBlogPost: (projectId: string, postId: string): Promise<AxiosResponse<ApiResponse<BlogPost>>> =>
    api.get(`/blog/projects/${projectId}/posts/${postId}`),

  /** Create a new blog post */
  createBlogPost: (
    projectId: string,
    data: CreateBlogPostData
  ): Promise<AxiosResponse<ApiResponse<BlogPost>>> =>
    api.post(`/blog/projects/${projectId}/posts`, data),

  /** Update a blog post */
  updateBlogPost: (
    projectId: string,
    postId: string,
    data: UpdateBlogPostData
  ): Promise<AxiosResponse<ApiResponse<BlogPost>>> =>
    api.put(`/blog/projects/${projectId}/posts/${postId}`, data),

  /** Delete a blog post */
  deleteBlogPost: (
    projectId: string,
    postId: string
  ): Promise<AxiosResponse<ApiResponse<{ deleted: boolean }>>> =>
    api.delete(`/blog/projects/${projectId}/posts/${postId}`),

  /** Get the project's auto-deduced (editable) industry profile */
  getIndustryProfile: (
    projectId: string
  ): Promise<AxiosResponse<ApiResponse<{ profile: IndustryProfile }>>> =>
    api.get(`/blog/projects/${projectId}/business-profile`),

  /** Edit the industry profile (marks it user-edited so auto-resolve won't overwrite) */
  updateIndustryProfile: (
    projectId: string,
    data: Partial<
      Pick<IndustryProfile, 'industry' | 'productCategories' | 'targetAudience' | 'keywords'>
    >
  ): Promise<AxiosResponse<ApiResponse<{ profile: IndustryProfile }>>> =>
    api.patch(`/blog/projects/${projectId}/business-profile`, data),

  /** List trend-driven blog suggestions + quota usage */
  getBlogSuggestions: (
    projectId: string,
    params?: { status?: 'pending' | 'generated' | 'dismissed' }
  ): Promise<AxiosResponse<ApiResponse<BlogSuggestionsResponse>>> =>
    api.get(`/blog/projects/${projectId}/suggestions`, { params }),

  /** Research + queue a fresh batch of suggestions now */
  refreshBlogSuggestions: (
    projectId: string
  ): Promise<AxiosResponse<ApiResponse<{ created: number }>>> =>
    api.post(`/blog/projects/${projectId}/suggestions/refresh`),

  /** Queue the admin's own topic for the Editorial Desk (top priority, quota-exempt) */
  createBlogSuggestion: (
    projectId: string,
    data: ManualBlogTopicInput
  ): Promise<AxiosResponse<ApiResponse<{ suggestion: BlogSuggestion }>>> =>
    api.post(`/blog/projects/${projectId}/suggestions`, data),

  /** Generate the full blog post from a suggestion (draft, or publish=true) */
  generateBlogSuggestion: (
    projectId: string,
    suggestionId: string,
    publish = false
  ): Promise<AxiosResponse<ApiResponse<{ postId: string; slug: string; status: string }>>> =>
    api.post(`/blog/projects/${projectId}/suggestions/${suggestionId}/generate`, undefined, {
      params: { publish: publish ? 'true' : undefined },
    }),

  /** Dismiss a pending suggestion */
  getBlogTopicMap: (projectId: string): Promise<AxiosResponse<ApiResponse<TopicMapResponse>>> =>
    api.get(`/blog/projects/${projectId}/topic-map`),
  runBlogTopicEngine: (
    projectId: string,
    regenerate = false
  ): Promise<
    AxiosResponse<ApiResponse<{ created: number; summary: string; regenerated: boolean }>>
  > => api.post(`/blog/projects/${projectId}/topic-map/run${regenerate ? '?regenerate=true' : ''}`),
  updateBlogTopicNode: (
    projectId: string,
    nodeId: string,
    status: 'planned' | 'dismissed'
  ): Promise<AxiosResponse<ApiResponse<{ nodeId: string; status: string }>>> =>
    api.patch(`/blog/projects/${projectId}/topic-map/nodes/${nodeId}`, { status }),
  refreshBlogTopicMetrics: (
    projectId: string
  ): Promise<AxiosResponse<ApiResponse<{ posts: number; rewrites: number; skipped?: string }>>> =>
    api.post(`/blog/projects/${projectId}/topic-map/metrics/refresh`),
  dismissBlogSuggestion: (
    projectId: string,
    suggestionId: string
  ): Promise<AxiosResponse<ApiResponse<{ dismissed: boolean }>>> =>
    api.post(`/blog/projects/${projectId}/suggestions/${suggestionId}/dismiss`),

  /** Get blog categories for a project */
  getBlogCategories: (projectId: string): Promise<AxiosResponse<ApiResponse<BlogCategory[]>>> =>
    api.get(`/blog/projects/${projectId}/categories`),

  /** Create a new blog category */
  createBlogCategory: (
    projectId: string,
    data: CreateBlogCategoryData
  ): Promise<AxiosResponse<ApiResponse<BlogCategory>>> =>
    api.post(`/blog/projects/${projectId}/categories`, data),

  /** Update a blog category */
  updateBlogCategory: (
    projectId: string,
    categoryId: string,
    data: Partial<CreateBlogCategoryData>
  ): Promise<AxiosResponse<ApiResponse<BlogCategory>>> =>
    api.put(`/blog/projects/${projectId}/categories/${categoryId}`, data),

  /** Delete a blog category */
  deleteBlogCategory: (
    projectId: string,
    categoryId: string
  ): Promise<AxiosResponse<ApiResponse<{ deleted: boolean; postsUncategorized?: number }>>> =>
    api.delete(`/blog/projects/${projectId}/categories/${categoryId}`),

  /** Move every post from `categoryId` onto `targetId`, then delete the source. */
  mergeBlogCategory: (
    projectId: string,
    categoryId: string,
    targetId: string
  ): Promise<AxiosResponse<ApiResponse<{ merged: boolean; postsMoved: number }>>> =>
    api.post(`/blog/projects/${projectId}/categories/${categoryId}/merge`, { targetId }),

  /** Get blog tags for a project */
  getBlogTags: (projectId: string): Promise<AxiosResponse<ApiResponse<BlogTag[]>>> =>
    api.get(`/blog/projects/${projectId}/tags`),

  /** Create a new blog tag */
  createBlogTag: (
    projectId: string,
    data: CreateBlogTagData
  ): Promise<AxiosResponse<ApiResponse<BlogTag>>> =>
    api.post(`/blog/projects/${projectId}/tags`, data),

  /** Delete a blog tag */
  deleteBlogTag: (
    projectId: string,
    tagId: string
  ): Promise<AxiosResponse<ApiResponse<{ deleted: boolean }>>> =>
    api.delete(`/blog/projects/${projectId}/tags/${tagId}`),

  // -------------------------------------------------------------------------
  // Custom Domains
  // -------------------------------------------------------------------------

  /** List custom domains for a project */
  getDomains: (projectId: string): Promise<AxiosResponse<ApiResponse<any[]>>> =>
    api.get(`/domains/${projectId}`),

  /** Add a custom domain */
  addDomain: (projectId: string, domain: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/domains/${projectId}`, { domain }),

  /** Check domain verification status */
  checkDomainStatus: (
    projectId: string,
    domain: string
  ): Promise<AxiosResponse<ApiResponse<any>>> => api.get(`/domains/${projectId}/${domain}/status`),

  /** Trigger domain re-verification */
  verifyDomain: (projectId: string, domain: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/domains/${projectId}/${domain}/verify`),

  /** Remove a custom domain */
  removeDomain: (projectId: string, domain: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.delete(`/domains/${projectId}/${domain}`),

  /**
   * Look up a domain's DNS provider via authoritative NS records.
   * Returns { apex, nameservers[], provider, apexCnameSupported, isRegistered }.
   * Used by the Add Domain modal to give provider-specific apex guidance.
   */
  lookupDns: (domain: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(`/domains/lookup/${encodeURIComponent(domain)}`),

  // -------------------------------------------------------------------------
  // DNS Migration (Cloudflare nameserver takeover wizard)
  // -------------------------------------------------------------------------

  /** Start a new DNS migration for an apex domain. Validates the CF token
   *  and returns accounts the token can access. */
  startDnsMigration: (
    projectId: string,
    data: { domain: string; token: string; hostnameMode?: 'apex' | 'www' | 'both' }
  ): Promise<
    AxiosResponse<
      ApiResponse<{ migration: DnsMigration; accounts: { id: string; name: string }[] }>
    >
  > => api.post(`/dns-migration/${projectId}/start`, data),

  /** Choose which CF account the zone will be created in. */
  chooseDnsMigrationAccount: (
    projectId: string,
    migrationId: string,
    data: { accountId: string; accountName?: string }
  ): Promise<AxiosResponse<ApiResponse<DnsMigration>>> =>
    api.post(`/dns-migration/${projectId}/${migrationId}/account`, data),

  /** Re-run the CF auto-import step (only needed if auto-import failed). */
  importDnsMigrationRecords: (
    projectId: string,
    migrationId: string
  ): Promise<AxiosResponse<ApiResponse<DnsMigration>>> =>
    api.post(`/dns-migration/${projectId}/${migrationId}/import`),

  /** Add Tyashin's required records (CNAME/TXT) on top of imported DNS. */
  addDnsMigrationRecords: (
    projectId: string,
    migrationId: string
  ): Promise<AxiosResponse<ApiResponse<DnsMigration>>> =>
    api.post(`/dns-migration/${projectId}/${migrationId}/add-records`),

  /** Poll migration status. Backend may advance state (waiting_for_ns → zone_activating). */
  getDnsMigrationStatus: (
    projectId: string,
    migrationId: string
  ): Promise<AxiosResponse<ApiResponse<DnsMigration>>> =>
    api.get(`/dns-migration/${projectId}/${migrationId}/status`),

  /** Finalize: register the hostname with Tyashin once the CF zone is active. */
  finalizeDnsMigration: (
    projectId: string,
    migrationId: string
  ): Promise<AxiosResponse<ApiResponse<DnsMigration>>> =>
    api.post(`/dns-migration/${projectId}/${migrationId}/finalize`),

  /** Abort an in-progress migration. */
  abortDnsMigration: (
    projectId: string,
    migrationId: string
  ): Promise<AxiosResponse<ApiResponse<DnsMigration>>> =>
    api.post(`/dns-migration/${projectId}/${migrationId}/abort`),

  /** List all DNS migrations for a project. */
  listDnsMigrations: (projectId: string): Promise<AxiosResponse<ApiResponse<DnsMigration[]>>> =>
    api.get(`/dns-migration/${projectId}`),

  // -------------------------------------------------------------------------
  // Analytics
  // -------------------------------------------------------------------------

  /** Get analytics summary for a project */
  getAnalyticsSummary: (
    projectId: string,
    period?: string
  ): Promise<AxiosResponse<ApiResponse<AnalyticsSummary>>> =>
    api.get(`/analytics/${projectId}/summary`, { params: { period } }),

  /** Get top pages */
  getAnalyticsTopPages: (
    projectId: string,
    period?: string,
    limit?: number
  ): Promise<AxiosResponse<ApiResponse<TopPage[]>>> =>
    api.get(`/analytics/${projectId}/top-pages`, { params: { period, limit } }),

  /** Get traffic sources */
  getAnalyticsSources: (
    projectId: string,
    period?: string,
    limit?: number
  ): Promise<AxiosResponse<ApiResponse<TrafficSource[]>>> =>
    api.get(`/analytics/${projectId}/sources`, { params: { period, limit } }),

  /** Get device breakdown */
  getAnalyticsDevices: (
    projectId: string,
    period?: string
  ): Promise<AxiosResponse<ApiResponse<DeviceBreakdown[]>>> =>
    api.get(`/analytics/${projectId}/devices`, { params: { period } }),

  /** Get browsers breakdown */
  getAnalyticsBrowsers: (
    projectId: string,
    period?: string
  ): Promise<AxiosResponse<ApiResponse<BrowserBreakdown[]>>> =>
    api.get(`/analytics/${projectId}/browsers`, { params: { period } }),

  /** Get time series data */
  getAnalyticsTimeseries: (
    projectId: string,
    period?: string
  ): Promise<AxiosResponse<ApiResponse<TimeseriesData>>> =>
    api.get(`/analytics/${projectId}/timeseries`, { params: { period } }),

  /** Get recent events */
  getAnalyticsRecentEvents: (
    projectId: string,
    limit?: number
  ): Promise<AxiosResponse<ApiResponse<RecentEvent[]>>> =>
    api.get(`/analytics/${projectId}/recent`, { params: { limit } }),

  // -------------------------------------------------------------------------
  // FAQ (storefront FAQ page admin)
  // -------------------------------------------------------------------------

  listFaqEntries: (projectId: string): Promise<AxiosResponse<ApiResponse<FaqEntry[]>>> =>
    api.get(`/projects/${projectId}/faq`),

  createFaqEntry: (
    projectId: string,
    data: FaqEntryInput
  ): Promise<AxiosResponse<ApiResponse<FaqEntry>>> => api.post(`/projects/${projectId}/faq`, data),

  updateFaqEntry: (
    projectId: string,
    id: string,
    data: Partial<FaqEntryInput> & { order?: number }
  ): Promise<AxiosResponse<ApiResponse<FaqEntry>>> =>
    api.patch(`/projects/${projectId}/faq/${id}`, data),

  deleteFaqEntry: (projectId: string, id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.delete(`/projects/${projectId}/faq/${id}`),

  reorderFaqEntries: (
    projectId: string,
    ids: string[]
  ): Promise<AxiosResponse<ApiResponse<{ modified: number }>>> =>
    api.post(`/projects/${projectId}/faq/reorder`, { ids }),

  listFaqTemplates: (projectId: string): Promise<AxiosResponse<ApiResponse<FaqTemplateSet[]>>> =>
    api.get(`/projects/${projectId}/faq/templates`),

  seedFaqFromTemplate: (
    projectId: string,
    data: { set: string; includeDerived?: boolean; merge?: boolean }
  ): Promise<
    AxiosResponse<
      ApiResponse<{
        inserted: number;
        skipped: number;
        entries: Array<{ templateKey: string; question: string; category?: string }>;
      }>
    >
  > => api.post(`/projects/${projectId}/faq/seed`, data),

  // -------------------------------------------------------------------------
  // Instagram Reels (instagram-reels plugin — curation/moderation)
  // -------------------------------------------------------------------------

  listInstagramReels: (
    projectId: string,
    params?: {
      status?: string;
      placement?: string;
      productId?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<AxiosResponse<ApiResponse<InstagramReel[]>>> =>
    api.get(`/projects/${projectId}/instagram-reels`, { params }),

  getInstagramReelStats: (
    projectId: string
  ): Promise<AxiosResponse<ApiResponse<{ pending: number; approved: number; rejected: number }>>> =>
    api.get(`/projects/${projectId}/instagram-reels/stats`),

  createInstagramReel: (
    projectId: string,
    data: {
      url: string;
      caption?: string;
      credit?: string;
      placements?: Array<'home' | 'product'>;
      productIds?: string[];
    }
  ): Promise<AxiosResponse<ApiResponse<InstagramReel>>> =>
    api.post(`/projects/${projectId}/instagram-reels`, data),

  updateInstagramReel: (
    projectId: string,
    id: string,
    data: Partial<{
      status: 'pending' | 'approved' | 'rejected';
      caption: string;
      credit: string;
      placements: Array<'home' | 'product'>;
      productIds: string[];
      order: number;
    }>
  ): Promise<AxiosResponse<ApiResponse<InstagramReel>>> =>
    api.patch(`/projects/${projectId}/instagram-reels/${id}`, data),

  reorderInstagramReels: (
    projectId: string,
    ids: string[]
  ): Promise<AxiosResponse<ApiResponse<{ updated: number }>>> =>
    api.post(`/projects/${projectId}/instagram-reels/reorder`, { ids }),

  deleteInstagramReel: (
    projectId: string,
    id: string
  ): Promise<AxiosResponse<ApiResponse<{ deleted: boolean }>>> =>
    api.delete(`/projects/${projectId}/instagram-reels/${id}`),

  // -------------------------------------------------------------------------
  // Testimonials (testimonials plugin — invite links + moderation)
  // -------------------------------------------------------------------------

  listTestimonials: (
    projectId: string,
    params?: { status?: string; page?: number; limit?: number }
  ): Promise<AxiosResponse<ApiResponse<Testimonial[]>>> =>
    api.get(`/projects/${projectId}/testimonials`, { params }),

  getTestimonialStats: (
    projectId: string
  ): Promise<AxiosResponse<ApiResponse<{ pending: number; approved: number; rejected: number }>>> =>
    api.get(`/projects/${projectId}/testimonials/stats`),

  /** Moderation only: the words, name and rating are not editable by design. */
  moderateTestimonial: (
    projectId: string,
    id: string,
    data: Partial<{ status: Testimonial['status']; featured: boolean; order: number }>
  ): Promise<AxiosResponse<ApiResponse<Testimonial>>> =>
    api.patch(`/projects/${projectId}/testimonials/${id}`, data),

  reorderTestimonials: (
    projectId: string,
    ids: string[]
  ): Promise<AxiosResponse<ApiResponse<{ updated: number }>>> =>
    api.post(`/projects/${projectId}/testimonials/reorder`, { ids }),

  deleteTestimonial: (
    projectId: string,
    id: string
  ): Promise<AxiosResponse<ApiResponse<{ deleted: boolean }>>> =>
    api.delete(`/projects/${projectId}/testimonials/${id}`),

  listTestimonialInvites: (
    projectId: string
  ): Promise<AxiosResponse<ApiResponse<TestimonialInvite[]>>> =>
    api.get(`/projects/${projectId}/testimonials/invites`),

  /** Returns the link ONCE (`url`); only its hash is stored server-side. */
  createTestimonialInvite: (
    projectId: string,
    data: { recipientName?: string; label?: string; expiresInDays?: number; maxUses?: number }
  ): Promise<AxiosResponse<ApiResponse<TestimonialInvite & { url: string; enabled: boolean }>>> =>
    api.post(`/projects/${projectId}/testimonials/invites`, data),

  revokeTestimonialInvite: (
    projectId: string,
    id: string
  ): Promise<AxiosResponse<ApiResponse<{ revoked: boolean }>>> =>
    api.delete(`/projects/${projectId}/testimonials/invites/${id}`),

  // -------------------------------------------------------------------------
  // Bulk image import v2 — filename→product mapping proposal
  // -------------------------------------------------------------------------

  matchBulkImageFilenames: (
    projectId: string,
    filenames: string[]
  ): Promise<AxiosResponse<ApiResponse<BulkImageMatchData>>> =>
    api.post(`/projects/${projectId}/ecommerce/products/bulk/images/match`, { filenames }),

  // -------------------------------------------------------------------------
  // Privacy Requests (GDPR / CCPA admin queue)
  // -------------------------------------------------------------------------

  listPrivacyRequests: (
    projectId: string,
    params?: { status?: string; overdue?: 'true' | 'false'; type?: string; limit?: number }
  ): Promise<AxiosResponse<ApiResponse<PrivacyRequest[]>>> =>
    api.get(`/projects/${projectId}/privacy-requests`, { params }),

  getPrivacyRequest: (
    projectId: string,
    id: string
  ): Promise<AxiosResponse<ApiResponse<PrivacyRequest>>> =>
    api.get(`/projects/${projectId}/privacy-requests/${id}`),

  advancePrivacyRequest: (
    projectId: string,
    id: string,
    data: { status: string; note?: string }
  ): Promise<AxiosResponse<ApiResponse<PrivacyRequest>>> =>
    api.post(`/projects/${projectId}/privacy-requests/${id}/advance`, data),

  updatePrivacyRequestNote: (
    projectId: string,
    id: string,
    data: { internalNote?: string }
  ): Promise<AxiosResponse<ApiResponse<PrivacyRequest>>> =>
    api.patch(`/projects/${projectId}/privacy-requests/${id}`, data),

  exportPrivacyRequest: (projectId: string, id: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(`/projects/${projectId}/privacy-requests/${id}/export`),

  // -------------------------------------------------------------------------
  // Reviews moderation
  // -------------------------------------------------------------------------

  listReviews: (
    projectId: string,
    params?: { status?: string; productId?: string; minRating?: number; limit?: number }
  ): Promise<AxiosResponse<ApiResponse<ProductReview[]>>> =>
    api.get(`/projects/${projectId}/reviews`, { params }),

  moderateReview: (
    projectId: string,
    id: string,
    data: { status: 'published' | 'rejected'; note?: string }
  ): Promise<AxiosResponse<ApiResponse<ProductReview>>> =>
    api.post(`/projects/${projectId}/reviews/${id}/moderate`, data),

  replyToReview: (
    projectId: string,
    id: string,
    data: { body: string }
  ): Promise<AxiosResponse<ApiResponse<ProductReview>>> =>
    api.post(`/projects/${projectId}/reviews/${id}/reply`, data),

  removeReviewReply: (
    projectId: string,
    id: string
  ): Promise<AxiosResponse<ApiResponse<ProductReview>>> =>
    api.delete(`/projects/${projectId}/reviews/${id}/reply`),

  deleteReview: (projectId: string, id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.delete(`/projects/${projectId}/reviews/${id}`),

  // -------------------------------------------------------------------------
  // Returns (RMA queue)
  // -------------------------------------------------------------------------

  listReturns: (
    projectId: string,
    params?: { status?: string; orderNumber?: string; email?: string; limit?: number }
  ): Promise<AxiosResponse<ApiResponse<ReturnRequest[]>>> =>
    api.get(`/projects/${projectId}/returns`, { params }),

  getReturn: (projectId: string, id: string): Promise<AxiosResponse<ApiResponse<ReturnRequest>>> =>
    api.get(`/projects/${projectId}/returns/${id}`),

  advanceReturn: (
    projectId: string,
    id: string,
    data: { status: string; note?: string; refundAmount?: number }
  ): Promise<AxiosResponse<ApiResponse<ReturnRequest>>> =>
    api.post(`/projects/${projectId}/returns/${id}/advance`, data),

  updateReturnNote: (
    projectId: string,
    id: string,
    data: { internalNote?: string }
  ): Promise<AxiosResponse<ApiResponse<ReturnRequest>>> =>
    api.patch(`/projects/${projectId}/returns/${id}`, data),

  // -------------------------------------------------------------------------
  // SEO verification tokens (Google Search Console / Bing / IndexNow)
  // -------------------------------------------------------------------------

  getProjectSeo: (projectId: string): Promise<AxiosResponse<ApiResponse<ProjectSeoConfig>>> =>
    api.get(`/projects/${projectId}/seo`),

  updateProjectSeo: (
    projectId: string,
    data: Partial<ProjectSeoConfig>
  ): Promise<AxiosResponse<ApiResponse<ProjectSeoConfig>>> =>
    api.patch(`/projects/${projectId}/seo`, data),

  // -------------------------------------------------------------------------
  // SEO Co-Pilot plugin (Phase 3+4+5)
  // -------------------------------------------------------------------------

  seoCopilotAudits: (projectId: string) =>
    api.get(`/plugins/seo-copilot/projects/${projectId}/audits`),
  seoCopilotRunAudit: (projectId: string) =>
    api.post(`/plugins/seo-copilot/projects/${projectId}/audits/run`),
  seoCopilotOta: (projectId: string) => api.get(`/plugins/seo-copilot/projects/${projectId}/ota`),
  seoCopilotGbpAuthorize: (projectId: string) =>
    api.get(`/plugins/seo-copilot/projects/${projectId}/gbp/authorize`),
  seoCopilotListOutreach: (projectId: string) =>
    api.get(`/plugins/seo-copilot/projects/${projectId}/outreach`),
  seoCopilotAddOutreach: (
    projectId: string,
    data: { name: string; email?: string; url: string; note?: string; targetUrl: string }
  ) => api.post(`/plugins/seo-copilot/projects/${projectId}/outreach`, data),
  seoCopilotDraftOutreach: (projectId: string, prospectId: string) =>
    api.post(`/plugins/seo-copilot/projects/${projectId}/outreach/${prospectId}/draft`),
  seoCopilotSendOutreach: (projectId: string, prospectId: string) =>
    api.post(`/plugins/seo-copilot/projects/${projectId}/outreach/${prospectId}/send`),
  /** Re-pull SEO settings from project + live-site data (fill-only). */
  seoCopilotResync: (projectId: string) =>
    api.post(`/plugins/projects/${projectId}/seo-copilot/resync`),
  /** Thridify: re-run the account link sync (account hash, domain allowlist, schema + catalog push). */
  thridifyResync: (projectId: string) => api.post(`/plugins/projects/${projectId}/thridify/resync`),
  /** Thridify: the owner's experience-switcher curation + published experiences (proxied — lives in Thridify). */
  thridifyGetSwitcher: (projectId: string) =>
    api.get(`/plugins/projects/${projectId}/thridify/switcher`),
  thridifySaveSwitcher: (
    projectId: string,
    switcher: { enabled: boolean; presentOn?: string[] } | null
  ) => api.put(`/plugins/projects/${projectId}/thridify/switcher`, { switcher }),
  /** Thridify enterprise sandbox: current environment + entitlement + link state. */
  thridifyGetEnvironment: (projectId: string) =>
    api.get(`/plugins/projects/${projectId}/thridify/environment`),
  thridifySetEnvironment: (projectId: string, environment: 'production' | 'sandbox') =>
    api.put(`/plugins/projects/${projectId}/thridify/environment`, { environment }),
  /** Thridify: explicit per-environment disconnect ("log out") without uninstalling. */
  thridifyDisconnect: (projectId: string, environment: 'production' | 'sandbox') =>
    api.post(`/plugins/projects/${projectId}/thridify/disconnect`, { environment }),
  /** Thridify: (re)connect an environment's account (production reconnect or sandbox link). */
  thridifyStartLink: (projectId: string, environment: 'production' | 'sandbox') =>
    api.post(`/plugins/projects/${projectId}/thridify/link`, { environment }),
  thridifyStartSandboxLink: (projectId: string) =>
    api.post(`/plugins/projects/${projectId}/thridify/sandbox/link`),
  /** AI credit balance for SEO Co-Pilot (Pro+). */
  seoCopilotAiCredits: (projectId: string) =>
    api.get(`/plugins/seo-copilot/projects/${projectId}/ai-credits`),
  /** ✨ AI-research a target-audience line (Pro+, spends one credit). */
  seoCopilotResearchAudience: (projectId: string) =>
    api.post(`/plugins/seo-copilot/projects/${projectId}/research-audience`),

  // ── SEO Autopilot (agentic) ──────────────────────────────────────────────
  /** Autopilot state: config, researched keywords, pending proposals, action log. */
  seoCopilotGetAgentic: (projectId: string) =>
    api.get(`/plugins/seo-copilot/projects/${projectId}/agentic`),
  /** Enable/disable Autopilot + cadence (Pro+) + auto-apply (Business). */
  seoCopilotSetAgentic: (
    projectId: string,
    data: { enabled: boolean; cadenceDays?: number; autoApply?: boolean }
  ) => api.put(`/plugins/seo-copilot/projects/${projectId}/agentic`, data),
  /** Run the Autopilot loop now (Pro+) — fresh research + proposal. */
  seoCopilotRunAgentic: (projectId: string) =>
    api.post(`/plugins/seo-copilot/projects/${projectId}/agentic/run`),
  /** Approve a pending Autopilot proposal — applies it to the live site. */
  seoCopilotApproveProposal: (projectId: string, proposalId: string) =>
    api.post(`/plugins/seo-copilot/projects/${projectId}/proposals/${proposalId}/approve`),
  /** Reject a pending Autopilot proposal — discards it. */
  seoCopilotRejectProposal: (projectId: string, proposalId: string) =>
    api.post(`/plugins/seo-copilot/projects/${projectId}/proposals/${proposalId}/reject`),

  // ── AI Answer Visibility (AEO) ────────────────────────────────────────────
  /** Score, per-engine rates, history, setup, readiness, monthly probe usage. */
  aeoSummary: (projectId: string) => api.get(`/plugins/aeo/projects/${projectId}/summary`),
  /** Per-question × engine rates over the rolling window + gap→blog status. */
  aeoGrid: (projectId: string) => api.get(`/plugins/aeo/projects/${projectId}/grid`),
  /** Topics fed to the Blog plugin → post → before/after AI-answer outcome. */
  aeoContent: (projectId: string) => api.get(`/plugins/aeo/projects/${projectId}/content`),
  /** Stored answers + citations (drill-down). */
  aeoProbes: (
    projectId: string,
    params: { questionId?: string; engine?: string; limit?: number }
  ) => api.get(`/plugins/aeo/projects/${projectId}/probes`, { params }),
  /** Save brand / competitors / questions. Omit expectedVersion to overwrite. */
  aeoSaveSetup: (projectId: string, data: Record<string, unknown>) =>
    api.put(`/plugins/aeo/projects/${projectId}/setup`, data),
  /** Bounded synchronous run; response carries a human `message`. */
  aeoRunNow: (projectId: string) => api.post(`/plugins/aeo/projects/${projectId}/run`),
  /** Businesses AI names in answers that leave us out (not saved). */
  aeoSuggestCompetitors: (projectId: string) =>
    api.post(`/plugins/aeo/projects/${projectId}/competitors/suggest`),

  // ── Core Web Vitals (Starter+) ───────────────────────────────────────────
  /** Stored CWV snapshots (latest + history) + whether the PageSpeed key is set. */
  seoCopilotCwv: (projectId: string) => api.get(`/plugins/seo-copilot/projects/${projectId}/cwv`),
  /** Fetch a fresh CWV snapshot now (Starter+, ~5-15s PageSpeed call). */
  seoCopilotRefreshCwv: (projectId: string) =>
    api.post(`/plugins/seo-copilot/projects/${projectId}/cwv/refresh`),

  // ── Google Search Console (Pro+) ─────────────────────────────────────────
  /** GSC connection status (connected?, email, siteUrl, available). */
  seoCopilotGscStatus: (projectId: string) =>
    api.get(`/plugins/seo-copilot/projects/${projectId}/gsc/status`),
  /** Ranking reports — top queries + pages, last 28 days (Pro+). */
  seoCopilotGscReports: (projectId: string) =>
    api.get(`/plugins/seo-copilot/projects/${projectId}/gsc/reports`),
  /** Index coverage for key URLs via URL Inspection (Pro+). */
  seoCopilotGscCoverage: (projectId: string) =>
    api.get(`/plugins/seo-copilot/projects/${projectId}/gsc/coverage`),
  /** Submit the sitemap to Search Console (Pro+). */
  seoCopilotGscSubmitSitemap: (projectId: string) =>
    api.post(`/plugins/seo-copilot/projects/${projectId}/gsc/submit-sitemap`),

  // ── Search engines: one status, one setup job, one disconnect (Pro+) ──────
  /** Both engines' lifecycle state + IndexNow activity + domain readiness. */
  seoCopilotEngines: (projectId: string) =>
    api.get(`/plugins/seo-copilot/projects/${projectId}/engines`),
  /** Start the engine's OAuth round-trip; returns `{ authorizeUrl }`. */
  seoCopilotEngineAuthorize: (projectId: string, engine: 'google' | 'bing') =>
    api.get(
      `/plugins/seo-copilot/projects/${projectId}/${engine === 'google' ? 'gbp' : 'bing'}/authorize`
    ),
  /** "Retry now" / "Finish setup": re-run the verify → register → sitemap chain. */
  seoCopilotEngineSetup: (projectId: string, engine: 'google' | 'bing') =>
    api.post(`/plugins/seo-copilot/projects/${projectId}/engines/${engine}/setup`),
  seoCopilotEngineDisconnect: (projectId: string, engine: 'google' | 'bing') =>
    api.post(`/plugins/seo-copilot/projects/${projectId}/engines/${engine}/disconnect`),

  // ── Compliant index queue (Pro+) ─────────────────────────────────────────
  /** The active index queue (pending/tracking/error) + counts + daily budget. */
  seoCopilotIndexQueue: (projectId: string) =>
    api.get(`/plugins/seo-copilot/projects/${projectId}/gsc/index-queue`),
  /** Run a queue pass now: reconcile sitemap -> re-submit -> inspect up to budget. */
  seoCopilotReconcileIndexQueue: (projectId: string) =>
    api.post(`/plugins/seo-copilot/projects/${projectId}/gsc/index-queue/reconcile`),
  /** Google "Request indexing" worklist: top-10 not-indexed URLs, ranked, deep-linked. */
  seoCopilotRequestWorklist: (projectId: string) =>
    api.get(`/plugins/seo-copilot/projects/${projectId}/gsc/request-worklist`),
  /** The owner pressed "Request indexing" in Search Console for this URL. */
  seoCopilotMarkRequested: (projectId: string, url: string) =>
    api.post(`/plugins/seo-copilot/projects/${projectId}/gsc/request-worklist/requested`, { url }),
  /** Reprioritize a queued URL (lower = inspected sooner). */
  seoCopilotReprioritizeIndexUrl: (projectId: string, url: string, priority: number) =>
    api.post(`/plugins/seo-copilot/projects/${projectId}/gsc/index-queue/reprioritize`, {
      url,
      priority,
    }),
  /** Per-keyword rank tracking over time (latest position + delta + history). */
  seoCopilotRankTracking: (projectId: string) =>
    api.get(`/plugins/seo-copilot/projects/${projectId}/gsc/rank-tracking`),
};

// ---------------------------------------------------------------------------
// Shared admin types for the endpoints above
// ---------------------------------------------------------------------------

export interface InstagramReel {
  _id: string;
  reelId: string;
  url: string;
  embedUrl: string;
  caption?: string;
  credit?: string;
  source: 'admin' | 'submission' | 'channel';
  authorHandle?: string;
  authorVerified?: boolean;
  status: 'pending' | 'approved' | 'rejected';
  placements: Array<'home' | 'product'>;
  productIds: string[];
  order: number;
  createdAt: string;
  updatedAt: string;
  moderatedAt?: string;
}

export interface Testimonial {
  _id: string;
  body: string;
  authorName: string;
  displayName: string;
  namePreference: 'full' | 'first-initial' | 'initials' | 'anonymous';
  context?: string;
  rating?: number;
  status: 'pending' | 'approved' | 'rejected';
  featured: boolean;
  order: number;
  submittedAt: string;
  moderatedAt?: string;
  consent: { publish: true; at: string; textVersion: string };
}

export interface TestimonialInvite {
  _id: string;
  label?: string;
  recipientName?: string;
  maxUses: number;
  uses: number;
  expiresAt: string;
  createdAt: string;
  lastUsedAt?: string;
  state: 'active' | 'expired' | 'used' | 'revoked';
}

export interface BulkImageMatchProposal {
  filename: string;
  base: string;
  seriesIndex: number;
  matchType: 'sku' | 'id' | 'slug' | 'name' | null;
  productId: string | null;
  ambiguous: boolean;
  candidates?: string[];
}

export interface BulkImageMatchData {
  matches: BulkImageMatchProposal[];
  products: Array<{
    id: string;
    name: string;
    slug: string;
    sku?: string;
    imageCount: number;
    primaryImageUrl?: string;
  }>;
}

export interface FaqEntry {
  _id: string;
  projectId: string;
  question: string;
  body: string;
  category?: string;
  isActive: boolean;
  order: number;
  templateKey?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface FaqEntryInput {
  question: string;
  body: string;
  category?: string;
  isActive?: boolean;
}

export interface FaqTemplateSet {
  id: string;
  label: string;
  description: string;
  entryCount: number;
}

export interface PrivacyRequestStatusEntry {
  status: string;
  note?: string;
  changedBy: string;
  changedAt: string;
}

export interface PrivacyRequest {
  _id: string;
  projectId: string;
  requestType: 'export' | 'delete' | 'access' | 'rectify' | 'opt-out';
  status: 'pending' | 'verifying' | 'in_progress' | 'completed' | 'rejected';
  email: string;
  fullName?: string;
  detail?: string;
  createdAt: string;
  respondByAt: string;
  completedAt?: string;
  updatedAt: string;
  internalNote?: string;
  statusHistory?: PrivacyRequestStatusEntry[];
  overdue?: boolean;
}

export interface ProductReview {
  _id: string;
  projectId: string;
  productId: string;
  orderNumber?: string;
  customerName?: string;
  customerEmail?: string;
  rating: number;
  title?: string;
  body: string;
  status: 'pending' | 'published' | 'rejected';
  moderationNote?: string;
  reply?: {
    body: string;
    repliedAt: string;
    repliedBy: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ReturnStatusEntry {
  status: string;
  note?: string;
  changedBy: string;
  changedAt: string;
}

export interface ReturnRequest {
  _id: string;
  projectId: string;
  orderNumber: string;
  customerEmail: string;
  status: 'pending' | 'approved' | 'rejected' | 'item_received' | 'refunded' | 'cancelled';
  reason?: string;
  items?: Array<{ productId: string; name: string; quantity: number }>;
  refundAmount?: number;
  currency?: string;
  internalNote?: string;
  statusHistory?: ReturnStatusEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface ProjectSeoConfig {
  googleSiteVerification?: string;
  bingSiteVerification?: string;
  indexNowKey?: string;
}

// Analytics types
export interface AnalyticsSummary {
  enabled: boolean;
  message?: string;
  period?: string;
  stats?: {
    visitors: number;
    visitorsChange: number;
    pageViews: number;
    pageViewsChange: number;
    events: number;
    sessions: number;
    avgPagesPerSession: string;
  };
}

export interface TopPage {
  path: string;
  views: number;
  visitors: number;
  percentage: number;
}

export interface TrafficSource {
  source: string;
  visitors: number;
  percentage: number;
}

export interface DeviceBreakdown {
  type: string;
  visitors: number;
  percentage: number;
}

export interface BrowserBreakdown {
  browser: string;
  visitors: number;
  percentage: number;
}

export interface TimeseriesData {
  period: string;
  groupBy: string;
  series: Array<{
    date: string;
    pageViews: number;
    visitors: number;
  }>;
}

export interface RecentEvent {
  id: string;
  type: string;
  name: string;
  path: string;
  device: string;
  browser: string;
  timestamp: string;
}

// ============================================================================
// AI Generation Types & Helpers
// ============================================================================

export interface AIBlogGenerationPrompt {
  topic: string;
  targetAudience?: string;
  tone?: 'professional' | 'casual' | 'educational' | 'entertaining' | 'inspirational';
  keywords?: string[];
  wordCount?: number;
  includeStatistics?: boolean;
  includeFAQ?: boolean;
}

export interface AIGeneratedBlogPost {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  tags: string[];
  suggestedCategory: string;
  seo: {
    metaTitle: string;
    metaDescription: string;
  };
  featuredImageQuery: string;
  estimatedReadTime: number;
  suggestedImages?: AIUnsplashImage[];
}

export interface AIUnsplashImage {
  id: string;
  url: string;
  thumbnailUrl: string;
  alt: string;
  photographer: string;
  photographerUrl: string;
}

export interface AIBlogOutline {
  title: string;
  sections: string[];
  estimatedWordCount: number;
}

export interface AISEOSuggestions {
  titleSuggestions: string[];
  metaDescriptionSuggestions: string[];
  keywordSuggestions: string[];
  improvements: string[];
}

export interface AIKeywordSuggestions {
  keywords: string[];
  topic: string;
}

export interface AIImagePrompts {
  prompts: string[];
  style: string;
  note: string;
}

export interface AIGeneratedImage {
  url: string;
  revisedPrompt?: string;
  size: string;
  quality: string;
}

export interface AITopicSuggestion {
  title: string;
  reason: string;
}

export interface AITopicSuggestions {
  topics: AITopicSuggestion[];
  industry: string;
  goal: string;
  targetAudience?: string;
}

// ============================================================================
// Admin Cost Monitoring Types & Helpers
// ============================================================================

export interface CostSummary {
  summary: {
    totalJobs: number;
    completedJobs: number;
    erroredJobs: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalCostUsd: number;
    avgCostPerJob: number;
    maxCostJob: number;
    minCostJob: number;
  };
  stepBreakdown: Array<{
    step: string;
    totalCalls: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalCostUsd: number;
    avgDurationMs: number;
    avgCostUsd: number;
  }>;
}

export interface CostPipeline {
  id: string;
  projectId: string;
  prompt: string;
  status: string;
  progress: number;
  createdBy: string;
  tokenUsage: {
    totalInputTokens: number;
    totalOutputTokens: number;
    totalCostUsd: number;
    stepCount: number;
  } | null;
  createdAt: string;
  completedAt: string | null;
  error: string | null;
}

export interface CostPipelineDetail {
  id: string;
  projectId: string;
  prompt: string;
  status: string;
  progress: number;
  createdBy: string;
  createdAt: string;
  completedAt: string | null;
  error: { message: string; details?: string } | null;
  tokenUsage: {
    totalInputTokens: number;
    totalOutputTokens: number;
    totalCostUsd: number;
    steps: Array<{
      step: string;
      model: string;
      inputTokens: number;
      outputTokens: number;
      costUsd: number;
      durationMs: number;
    }>;
  } | null;
}

export interface CostByUser {
  userId: string;
  userName: string;
  userEmail: string;
  totalJobs: number;
  completedJobs: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostUsd: number;
  avgCostPerJob: number;
  lastJobAt: string;
}

export interface CostDaily {
  date: string;
  jobs?: number;
  calls?: number;
  totalInputTokens?: number;
  totalOutputTokens?: number;
  totalCostUsd: number;
}

export interface CostSourceBreakdown {
  source: string;
  totalCalls: number;
  totalCostUsd: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  successCount?: number;
  failureCount?: number;
}

export interface CostAllSources {
  grandTotal: {
    totalCalls: number;
    totalCostUsd: number;
  };
  sourceBreakdown: CostSourceBreakdown[];
}

export interface CostByUserAllSources {
  userId: string;
  userName: string;
  userEmail: string;
  totalCalls: number;
  totalCostUsd: number;
  sources: string[];
  lastCallAt: string;
}

export const adminCostHelpers = {
  /** Get aggregate cost summary */
  getSummary: (from?: string, to?: string): Promise<AxiosResponse<ApiResponse<CostSummary>>> =>
    api.get('/admin/costs/summary', { params: { from, to } }),

  /** List all pipelines with costs */
  getPipelines: (params?: {
    page?: number;
    limit?: number;
    sort?: string;
    order?: 'asc' | 'desc';
    userId?: string;
    status?: string;
  }): Promise<AxiosResponse<ApiResponse<CostPipeline[]>>> =>
    api.get('/admin/costs/pipelines', { params }),

  /** Get detailed cost breakdown for one job */
  getPipelineDetail: (jobId: string): Promise<AxiosResponse<ApiResponse<CostPipelineDetail>>> =>
    api.get(`/admin/costs/pipelines/${jobId}`),

  /** Get cost aggregated per user */
  getByUser: (from?: string, to?: string): Promise<AxiosResponse<ApiResponse<CostByUser[]>>> =>
    api.get('/admin/costs/by-user', { params: { from, to } }),

  /** Get daily cost trend */
  getDaily: (days?: number): Promise<AxiosResponse<ApiResponse<CostDaily[]>>> =>
    api.get('/admin/costs/daily', { params: { days } }),

  /** Get unified cost view across ALL AI subsystems */
  getAllSources: (
    from?: string,
    to?: string
  ): Promise<AxiosResponse<ApiResponse<CostAllSources>>> =>
    api.get('/admin/costs/all-sources', { params: { from, to } }),

  /** Get daily cost trend across ALL sources */
  getAllSourcesDaily: (days?: number): Promise<AxiosResponse<ApiResponse<CostDaily[]>>> =>
    api.get('/admin/costs/all-sources/daily', { params: { days } }),

  /** Get per-user cost across ALL sources */
  getAllSourcesByUser: (
    from?: string,
    to?: string
  ): Promise<AxiosResponse<ApiResponse<CostByUserAllSources[]>>> =>
    api.get('/admin/costs/all-sources/by-user', { params: { from, to } }),
};

// =============================================================================
// Admin: Integrations (superadmin-only HMAC signing secrets)
// =============================================================================

export interface IntegrationView {
  id: string;
  serviceId: string;
  displayName: string;
  status: 'active' | 'disabled';
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastUsedAt?: string;
  lastRotatedAt?: string;
  notes?: string;
  isThirdParty?: boolean;
  developerEmail?: string;
  /** Only returned on create / rotate / explicit reveal */
  secret?: string;
}

export const adminIntegrationHelpers = {
  list: (): Promise<AxiosResponse<ApiResponse<IntegrationView[]>>> =>
    api.get('/admin/integrations'),

  get: (id: string, reveal = false): Promise<AxiosResponse<ApiResponse<IntegrationView>>> =>
    api.get(`/admin/integrations/${id}`, { params: reveal ? { reveal: 'true' } : undefined }),

  create: (input: {
    serviceId: string;
    displayName: string;
    notes?: string;
    isThirdParty?: boolean;
    developerEmail?: string;
  }): Promise<
    AxiosResponse<{
      success: boolean;
      data: IntegrationView;
      secret: string;
      warning: string;
    }>
  > => api.post('/admin/integrations', input),

  reveal: (id: string): Promise<AxiosResponse<ApiResponse<IntegrationView>>> =>
    api.post(`/admin/integrations/${id}/reveal`),

  rotate: (
    id: string
  ): Promise<
    AxiosResponse<{
      success: boolean;
      data: IntegrationView;
      secret: string;
      warning: string;
    }>
  > => api.post(`/admin/integrations/${id}/rotate`),

  update: (
    id: string,
    input: {
      displayName?: string;
      notes?: string;
      status?: 'active' | 'disabled';
      developerEmail?: string;
    }
  ): Promise<AxiosResponse<ApiResponse<IntegrationView>>> =>
    api.put(`/admin/integrations/${id}`, input),

  delete: (id: string): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
    api.delete(`/admin/integrations/${id}`),
};

export const aiHelpers = {
  // Generate complete blog post from prompt
  generateBlogPost: (
    prompt: AIBlogGenerationPrompt
  ): Promise<AxiosResponse<ApiResponse<AIGeneratedBlogPost>>> =>
    api.post('/ai/blog/generate', prompt),

  // Generate quick outline
  generateOutline: (topic: string): Promise<AxiosResponse<ApiResponse<AIBlogOutline>>> =>
    api.post('/ai/blog/outline', { topic }),

  // Improve existing content
  improveContent: (
    content: string,
    instruction: string
  ): Promise<AxiosResponse<ApiResponse<{ content: string }>>> =>
    api.post('/ai/blog/improve', { content, instruction }),

  // Get SEO suggestions
  getSEOSuggestions: (
    content: string,
    currentTitle: string
  ): Promise<AxiosResponse<ApiResponse<AISEOSuggestions>>> =>
    api.post('/ai/blog/seo', { content, currentTitle }),

  // Suggest keywords for a topic
  suggestKeywords: (
    topic: string,
    count?: number
  ): Promise<AxiosResponse<ApiResponse<AIKeywordSuggestions>>> =>
    api.post('/ai/blog/keywords', { topic, count }),

  // Suggest blog topics based on industry and goal
  suggestTopics: (
    industry: string,
    goal: 'traffic' | 'leads' | 'authority' | 'awareness',
    targetAudience?: string
  ): Promise<AxiosResponse<ApiResponse<AITopicSuggestions>>> =>
    api.post('/ai/blog/topics', { industry, goal, targetAudience }),

  // Search for images
  searchImages: (
    query: string,
    count?: number
  ): Promise<AxiosResponse<ApiResponse<AIUnsplashImage[]>>> =>
    api.post('/ai/images/search', { query, count }),

  // Generate image prompts
  generateImagePrompts: (
    prompt: string,
    style?: 'photo' | 'illustration' | 'digital-art' | '3d'
  ): Promise<AxiosResponse<ApiResponse<AIImagePrompts>>> =>
    api.post('/ai/images/generate', { prompt, style }),

  // Create image using DALL-E 3
  createImage: (
    prompt: string,
    size?: '1024x1024' | '1792x1024' | '1024x1792',
    quality?: 'standard' | 'hd'
  ): Promise<AxiosResponse<ApiResponse<AIGeneratedImage>>> =>
    api.post('/ai/images/create', { prompt, size, quality }),
};

// ---------------------------------------------------------------------------
// Generator Helpers
// ---------------------------------------------------------------------------

export const generatorHelpers = {
  /** List generation jobs for a project */
  getJobs: (projectId: string) => api.get(`/generator/${projectId}/jobs`),

  /** Get a specific generation job */
  getJob: (projectId: string, jobId: string) => api.get(`/generator/${projectId}/jobs/${jobId}`),

  /** Start a new generation */
  startGeneration: (
    projectId: string,
    data: { prompt: string; industry?: string; template?: string }
  ) => api.post(`/generator/${projectId}/start`, data),

  /** Send a refinement prompt */
  iterateJob: (projectId: string, jobId: string, data: { prompt: string }) =>
    api.post(`/generator/${projectId}/jobs/${jobId}/iterate`, data),

  /** Resume a failed/stalled job */
  resumeJob: (projectId: string, jobId: string) =>
    api.post(`/generator/${projectId}/jobs/${jobId}/resume`),

  /** Get cost estimate */
  estimate: (projectId: string, promptLength: number, assetsCount = 0) =>
    api.get(
      `/generator/${projectId}/estimate?promptLength=${promptLength}&assetsCount=${assetsCount}`
    ),

  /** Download generated files as ZIP */
  exportZip: (projectId: string, jobId: string) =>
    api.get(`/generator/${projectId}/jobs/${jobId}/export`, { responseType: 'blob' }),

  /** List available templates */
  getTemplates: () => api.get('/generator/templates'),

  /** Get storefront integration spec */
  getStorefrontSpec: (format: 'json' | 'text' = 'json') =>
    api.get(`/generator/storefront-spec${format === 'text' ? '?format=text' : ''}`),

  /** Import an external website from a ZIP upload */
  importWebsite: (projectId: string, file: File, source: string = 'manual', label?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', source);
    if (label) formData.append('label', label);
    return api.post(`/generator/${projectId}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000, // 2 min — large ZIPs take time
    });
  },
};

// ---------------------------------------------------------------------------
// Website Studio Helpers — the customer journey (brief → agent run → live site)
// ---------------------------------------------------------------------------

export interface StudioIntake {
  businessName?: string;
  tagline?: string;
  archetype?: 'ecommerce' | 'local-service' | 'b2b-catalogue' | 'marketing';
  industry?: string;
  offerings?: string;
  audience?: string;
  locations?: string;
  contact?: { phone?: string; whatsapp?: string; email?: string; address?: string; hours?: string };
  pages?: string[];
  style?: { mood?: string; colors?: string; references?: string[] };
  mustHaves?: string;
  avoid?: string;
  language?: string;
  existingWebsite?: string;
}

export type StudioPhase =
  | 'queued'
  | 'provisioning'
  | 'understanding'
  | 'designing'
  | 'building'
  | 'verifying'
  | 'publishing'
  | 'deploying'
  | 'done'
  | 'failed'
  | 'cancelled'
  | 'needs_credits';

export interface StudioRun {
  id: string;
  kind: 'generate' | 'edit';
  phase: StudioPhase;
  phaseLabel: string;
  progress: number;
  status: string;
  prompt: string;
  summary?: string;
  error?: { code: string; message: string };
  credits: { used: number; reserved: number; charged: number };
  filesTouched: number;
  siteUrl?: string;
  lastSeq: number;
  timeline: Array<{ type: string; at: string; data?: Record<string, unknown> }>;
  cancelRequested: boolean;
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  parentRunId?: string;
}

export interface StudioOverview {
  project: {
    id: string;
    name: string;
    slug: string;
    siteUrl: string;
    customDomain?: string;
    hasSite: boolean;
    deployed: boolean;
    buildStatus?: string;
    lastDeployedAt?: string;
    intake?: StudioIntake;
    onboardingCompletedAt?: string;
  };
  activeRun: StudioRun | null;
  lastRun: StudioRun | null;
  runs: StudioRun[];
  credits: {
    available: number;
    balance: number;
    expiring: number;
    expiresAt: string;
    reserveFor: { generate: number; edit: number };
  };
  runnerAvailable: boolean;
}

export interface StudioEvent {
  v: 1;
  seq: number;
  runId: string;
  projectId: string;
  type: string;
  at: string;
  data?: Record<string, unknown>;
}

export const studioHelpers = {
  overview: (projectId: string) =>
    api.get(`/projects/${projectId}/website`).then((r) => r.data.data as StudioOverview),
  updateSettings: (
    projectId: string,
    body: { name?: string; slug?: string; intake?: StudioIntake; onboardingCompleted?: boolean }
  ) => api.patch(`/projects/${projectId}/website/settings`, body).then((r) => r.data.data),
  startRun: (
    projectId: string,
    body: {
      kind: 'generate' | 'edit';
      prompt: string;
      intake?: StudioIntake;
      confirmReplace?: boolean;
      resumeRunId?: string;
    }
  ) => api.post(`/projects/${projectId}/website/runs`, body).then((r) => r.data.data as StudioRun),
  listRuns: (projectId: string) =>
    api.get(`/projects/${projectId}/website/runs`).then((r) => r.data.data as StudioRun[]),
  getRun: (projectId: string, runId: string) =>
    api.get(`/projects/${projectId}/website/runs/${runId}`).then((r) => r.data.data as StudioRun),
  events: (projectId: string, runId: string, after = 0) =>
    api
      .get(`/projects/${projectId}/website/runs/${runId}/events`, { params: { after } })
      .then((r) => r.data.data as { events: StudioEvent[]; lastSeq: number; run: StudioRun }),
  cancelRun: (projectId: string, runId: string) =>
    api
      .post(`/projects/${projectId}/website/runs/${runId}/cancel`)
      .then((r) => r.data.data as StudioRun),
  /** WebSocket URL for a run's live stream (auth via ?token= — browsers can't set headers on upgrades). */
  streamUrl: (projectId: string, runId: string, token: string, after = 0) => {
    const base = (import.meta.env.VITE_API_URL || '/api/v1') as string;
    const abs = base.startsWith('http') ? base : `${window.location.origin}${base}`;
    const u = new URL(
      `${abs.replace(/\/$/, '')}/projects/${projectId}/website/runs/${runId}/stream`
    );
    u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    u.searchParams.set('token', token);
    u.searchParams.set('after', String(after));
    return u.toString();
  },
};

export interface AiCreditWallet {
  balance: number;
  expiring: number;
  purchased: number;
  reserved: number;
  available: number;
  plan: string;
  monthlyAllowance: number;
  allowanceMonth?: string;
  expiresAt: string;
  currency: string;
  reserveFor: { generate: number; edit: number };
  packs: Array<{ id: string; label: string; credits: number; price: number; currency: string }>;
}

export const aiCreditHelpers = {
  wallet: () => api.get('/billing/ai-credits').then((r) => r.data.data as AiCreditWallet),
  ledger: () =>
    api.get('/billing/ai-credits/ledger').then(
      (r) =>
        r.data.data as Array<{
          id: string;
          type: string;
          amount: number;
          balanceAfter: number;
          description: string;
          createdAt: string;
        }>
    ),
  checkout: (packId: string, returnPath?: string) =>
    api
      .post('/billing/ai-credits/checkout', { packId, returnPath })
      .then((r) => r.data.data as { checkoutUrl: string; gateway: string; credits: number }),
};

// ---------------------------------------------------------------------------
// GitHub Integration Helpers
// ---------------------------------------------------------------------------

export const githubHelpers = {
  /** Get current GitHub connection for a project */
  getConnection: (projectId: string) => api.get(`/github/${projectId}/connection`),

  /** List accessible GitHub repos (after OAuth) */
  listRepos: (projectId: string, page = 1) => api.get(`/github/${projectId}/repos?page=${page}`),

  /** Set up repo + branch + create webhook */
  setup: (
    projectId: string,
    data: {
      repoOwner: string;
      repoName: string;
      branch: string;
      autoDeploy?: boolean;
      needsBuild?: boolean;
    }
  ) => api.post(`/github/${projectId}/setup`, data),

  /** Manual sync — re-import from GitHub now */
  sync: (projectId: string) => api.post(`/github/${projectId}/sync`),

  /** Update connection settings */
  updateSettings: (
    projectId: string,
    data: { autoDeploy?: boolean; needsBuild?: boolean; branch?: string }
  ) => api.patch(`/github/${projectId}/settings`, data),

  /** Disconnect GitHub repo */
  disconnect: (projectId: string) => api.delete(`/github/${projectId}/disconnect`),
};

// ============================================================================
// YOM-backed analytics (embed tokens, status, claim link)
// Contract: YOM-EVERYWHERE-ARCHITECTURE.md §7 / aapastech-backend/docs/analytics-embed-api.md
// ============================================================================

/** Every report the project Analytics page renders; the token is scoped to these. */
export const PROJECT_ANALYTICS_REPORTS = [
  'overview',
  'timeseries',
  'breakdown',
  'realtime',
  'ecommerce',
  'funnel',
] as const;

export const analyticsEmbedHelpers = {
  getStatus: async (projectId: string): Promise<ProjectAnalyticsStatus> =>
    normalizeAnalyticsStatus((await api.get(`/projects/${projectId}/analytics/status`)).data),

  getEmbedToken: async (projectId: string): Promise<TokenBundle> =>
    normalizeProjectEmbedToken(
      (
        await api.get(`/projects/${projectId}/analytics/embed-token`, {
          params: { reports: PROJECT_ANALYTICS_REPORTS.join(',') },
        })
      ).data
    ),

  createClaimLink: async (projectId: string): Promise<string> =>
    normalizeClaimLink((await api.post(`/projects/${projectId}/analytics/claim-link`)).data),

  /** SUPER_ADMIN only. Tokens for T-APP ('app') and T-SITES ('sites'). */
  getPlatformInsightsTokens: async (): Promise<TokenBundle> =>
    normalizePlatformTokens((await api.get('/admin/platform-insights/embed-token')).data),

  /** SUPER_ADMIN only. Computed KPI snapshots for a UTC date range. */
  getPlatformKpis: async (from: string, to: string): Promise<PlatformKpis> =>
    (await api.get('/admin/platform-insights/kpis', { params: { from, to } })).data.data,

  /** SUPER_ADMIN only. Re-runs the KPI rollup now (yesterday, gaps, today so far). */
  recomputePlatformKpis: async (): Promise<KpiRecomputeStats> =>
    (await api.post('/admin/platform-insights/kpis/recompute')).data.data,
};

export default api;
